

/**
 * Get user's geolocation using multiple methods:
 * 1. Browser geolocation API (most accurate)
 * 2. Firebase function with IP-based geolocation (fallback)
 * 3. Default location from localStorage or hardcoded
 */
export const getGeo = async () => {
  // First, try browser geolocation if available
  console.log('here!')
  if (navigator.geolocation) {
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 300000 // Cache for 5 minutes
          }
        );
      });

      const { latitude, longitude } = position.coords;

      // Reverse geocode using Google Maps (if available)
      if (window.google?.maps) {
        const geocoder = new window.google.maps.Geocoder();
        
        try {
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode(
              { location: { lat: latitude, lng: longitude } },
              (results, status) => {
                if (status === 'OK' && results[0]) {
                  resolve(results[0]);
                } else {
                  reject(new Error('Geocoding failed'));
                }
              }
            );
          });

          const addressComponents = result.address_components;
          let postalCode = '';
          let city = '';
          let region = '';
          let country = '';
          let countryCode = '';

          addressComponents.forEach(component => {
            if (component.types.includes('postal_code')) {
              postalCode = component.long_name;
            }
            if (component.types.includes('locality')) {
              city = component.long_name;
            }
            if (component.types.includes('administrative_area_level_1')) {
              region = component.long_name;
            }
            if (component.types.includes('country')) {
              country = component.long_name;
              countryCode = component.short_name;
            }
          });

          const locationData = {
            city,
            region,
            country,
            countryCode,
            postalCode,
            latitude,
            longitude,
            formatted: result.formatted_address,
            source: 'browser-geolocation'
          };

          // Save to localStorage
          localStorage.setItem('userLocation', JSON.stringify(locationData));
          
          return {
            success: true,
            location: locationData
          };
        } catch (geocodeError) {
          console.warn('Geocoding failed:', geocodeError);
          // Return basic location data without address
          return {
            success: true,
            location: {
              latitude,
              longitude,
              source: 'browser-geolocation-coords-only'
            }
          };
        }
      }

      // If Google Maps not available, return coordinates only
      return {
        success: true,
        location: {
          latitude,
          longitude,
          source: 'browser-geolocation-coords-only'
        }
      };

    } catch (geoError) {
      console.warn('Browser geolocation failed:', geoError.message);
      // Fall through to Firebase function
    }
  }

  // Fallback: Use Firebase function to get location from IP
  try {
    const functionUrl = 'https://us-central1-myxline-staging.cloudfunctions.net/getGeo'
    // Only works on prod url, emulator can access localhost
    //"http://127.0.0.1:5001/myxline-staging/us-central1/getGeo"


    const result = await fetch(functionUrl);

    const data = await result.json();
    console.log("✅ Geolocation response:", data);
    
    if (data.success) {
      // Save to localStorage
      localStorage.setItem('userLocation', JSON.stringify(data.location));
      
      return {
        success: true,
        location: data.location
      };
    }
    
    // If Firebase function returns fallback location
    return data;

  } catch (firebaseError) {
    console.error('Firebase geolocation failed:', firebaseError);
    
    // Last resort: Check localStorage or return default
    const saved = localStorage.getItem('userLocation');
    if (saved) {
      try {
        return {
          success: true,
          location: JSON.parse(saved),
          source: 'localStorage'
        };
      } catch (parseError) {
        console.error('Error parsing saved location:', parseError);
      }
    }

    // Ultimate fallback
    return {
      success: false,
      location: {
        city: 'Helsinki',
        region: 'Uusimaa',
        country: 'Finland',
        countryCode: 'FI',
        postalCode: '00100',
        latitude: 60.1699,
        longitude: 24.9384,
        formatted: 'Helsinki, Finland',
        source: 'default'
      }
    };
  }
};

/**
 * Get cached location from localStorage
 */
export const getCachedLocation = () => {
  try {
    const saved = localStorage.getItem('userLocation');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Error getting cached location:', error);
  }
  return null;
};

/**
 * Format location for display
 */
export const formatLocation = (location) => {
  if (!location) return ''
  
  if (location.formatted) return location.formatted;
  
  const parts = [];
  if (location.city) parts.push(location.city);
  if (location.region) parts.push(location.region);
  if (location.country) parts.push(location.country);
  
  return parts.length > 0 ? parts.join(', ') : ''
};

/**
 * Get postal code from location
 */
export const getPostalCode = (location) => {
  if (!location) return null;
  return location.postalCode || null;
};